/**
 * Create Challenge Helpers
 * Pure functions for challenge creation logic
 *
 * Submission Bridge: buildPicksData adapts UI's matchWinnerSlip/otherSlips model
 * to unified PickPayload[] for the backend.
 *
 * Senior Warnings:
 * - Double Pick: Backend has @@unique([challengeId, userId, matchId, marketId]) on ChallengePick.
 * - Normalization: predictionToValue returns Home/Draw/Away — must match settlement.helper.js.
 */

import { Platform } from 'react-native';
import { MAX_GAMES_PER_CHALLENGE } from '../constants/create.constants';
import type { PickSlip, Friend, Game } from '../types/create.types';
import { isMatchWinnerSlip } from '../types/create.types';
import { getSportDefinitionOrDefault } from '../../sports/sports.constants';
import type { Group } from '../../../types/challenges';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { isPremiumMarketIdHiddenOnIos } from './premiumMarketsIosDefer.helper';

export { resolveFriendUserId } from './resolveFriendUserId.helper';
export type { FriendWithInvitation, ResolvedFriend, ResolveFriendError } from './resolveFriendUserId.helper';

/**
 * Validate challenge creation inputs
 */
export const validateChallengeInputs = (
  selectedFriendId: string | null,
  selectedGroupId: string | null,
  pickSlips: unknown[],
  friend: Friend | undefined,
  _friendId: string | null,
  maxGames: number = MAX_GAMES_PER_CHALLENGE,
  // Open challenges (public board) have no named opponent, so skip the participant requirement.
  isOpen: boolean = false
): { valid: boolean; error?: string } => {
  const hasParticipant = selectedFriendId || selectedGroupId;
  if ((!hasParticipant && !isOpen) || pickSlips.length === 0) {
    return { valid: false };
  }

  // Validate game limit
  if (pickSlips.length > maxGames) {
    return {
      valid: false,
      error: `You can only add up to ${maxGames} games per challenge. Please remove some games and try again.`,
    };
  }

  // Friend-availability check does not apply to open challenges (no named friend).
  const isMonkeyChallenge = selectedFriendId === MONKEY_USER_ID;
  if (!isOpen && selectedFriendId && !friend && !isMonkeyChallenge) {
    return {
      valid: false,
      error: 'Selected friend is no longer available. Please refresh and try again.',
    };
  }

  return { valid: true };
};

/**
 * Build participant IDs for group challenge
 */
export const buildGroupParticipantIds = (
  currentUserId: string,
  group: Group
): string[] => {
  const cleanUserId =
    typeof currentUserId === 'string' ? currentUserId.trim() : currentUserId;
  return [
    cleanUserId,
    ...group.memberIds.filter((id) => id !== cleanUserId),
  ];
};

/**
 * Build participant IDs for friend challenge
 */
export const buildFriendParticipantIds = (
  currentUserId: string,
  friendId: string
): string[] => {
  if (friendId === MONKEY_USER_ID) {
    // Backend expects anonymous Monkey challenges as a single invited participant (without creator id).
    return [MONKEY_USER_ID];
  }
  const cleanUserId =
    typeof currentUserId === 'string' ? currentUserId.trim() : currentUserId;
  return [cleanUserId, friendId];
};

/** Unified pick payload for API (1X2 + other markets). */
export interface PickPayload {
  matchId: string;
  marketId: number;
  marketName: string;
  value: string;
  odd: number;
}


/** Normalize prediction to value. Must match settlement.helper.js resolveMatchWinner. */
function predictionToValue(pred: 'home' | 'draw' | 'away'): string {
  if (pred === 'home') return 'Home';
  if (pred === 'away') return 'Away';
  return 'Draw';
}

/**
 * 1X2 decimal odd for the slip — same source as usePickSlips totalPoints (list odds).
 * Match-winner slips often omit `odd` in UI state; resolve from Game when provided.
 */
export function resolveMatchWinnerOddFromGame(
  game: Game | undefined,
  prediction: 'home' | 'draw' | 'away',
): number {
  if (!game?.odds) return 1;
  // `?? 1` rather than a fallback price: a FIELD sport has no home/away odd, and this value is
  // STORED on the pick, so inventing one would settle a race at a price nobody was offered.
  if (prediction === 'home') return game.odds.home ?? 1;
  if (prediction === 'away') return game.odds.away ?? 1;
  // Whether a draw has a price depends on the PRIMARY MARKET, not the sport's final-score rule.
  if (!getSportDefinitionOrDefault(game.sport).primaryMarketHasDraw) return 1;
  return game.odds.draw ?? 3;
}

/** Build picks for one game from matchWinnerSlip + otherSlips (Submission Bridge adapter). */
function buildPicksForGame(
  matchWinnerSlip: PickSlip | null,
  otherSlips: PickSlip[],
  gameId: string,
  game: Game | undefined,
): PickPayload[] {
  const picks: PickPayload[] = [];

  if (matchWinnerSlip?.prediction) {
    const odd =
      game != null
        ? resolveMatchWinnerOddFromGame(game, matchWinnerSlip.prediction)
        : typeof matchWinnerSlip.odd === 'number' && matchWinnerSlip.odd > 0
          ? matchWinnerSlip.odd
          : 1;

    // The one-tap pick must carry the market of the GAME'S sport, not football's.
    // Stamping the football 1X2 id (1) unconditionally meant a basketball quick-pick was
    // submitted as a football 1X2 bet: the server accepts it, but no basketball resolver handles
    // market 1, so at settlement it resolves to null and the bet scores 0. It was the default,
    // one-tap path — not an edge case (specs/basketball-live-feed).
    const sport = getSportDefinitionOrDefault(game?.sport);

    picks.push({
      matchId: gameId,
      marketId: sport.primaryMarketId,
      marketName: sport.primaryMarketName,
      value: predictionToValue(matchWinnerSlip.prediction),
      odd,
    });
  }

  for (const s of otherSlips) {
    if (s.marketId != null && s.marketName != null && s.value != null && typeof s.odd === 'number') {
      picks.push({
        matchId: s.gameId,
        marketId: s.marketId,
        marketName: s.marketName,
        value: s.value,
        // `0` passes `typeof === 'number'`, so send the same "resolve this yourself" sentinel the
        // match-winner branch above uses. Both server entry points already ignore a client odd that
        // is not > 0; this just stops us claiming a price of zero on the way in.
        odd: s.odd > 0 ? s.odd : 1,
      });
    }
  }

  return picks;
}

/**
 * Build unified picks from pick slips (Submission Bridge).
 * Adapts UI's matchWinnerSlip/otherSlips model to PickPayload[] for backend.
 * Groups by gameId, then for each game: 1x2 from match-winner slip, others from otherSlips.
 *
 * @param games - When provided, 1X2 `odd` is taken from each game's list odds (Create UI / guest Arena).
 *                Omit in tests or when odds are unknown (falls back to slip.odd or 1).
 */
export const buildPicksData = (pickSlips: PickSlip[], games?: Game[]): PickPayload[] => {
  const gameById =
    games != null && games.length > 0 ? new Map(games.map((g) => [g.id, g])) : null;
  const gameIds = [...new Set(pickSlips.map((s) => s.gameId).filter(Boolean))];
  const result: PickPayload[] = [];

  for (const gameId of gameIds) {
    const game = gameById?.get(gameId);
    const matchWinnerSlip =
      pickSlips.find((s) => s.gameId === gameId && isMatchWinnerSlip(s)) ?? null;
    const otherSlips = pickSlips.filter(
      (s) => s.gameId === gameId && !isMatchWinnerSlip(s)
    );
    result.push(...buildPicksForGame(matchWinnerSlip, otherSlips, gameId, game));
  }

  return result.filter(
    (pick) => !(Platform.OS === 'ios' && isPremiumMarketIdHiddenOnIos(pick.marketId)),
  );
};

/**
 * Build challenge data payload (unified picks only).
 */
export const buildChallengeData = (
  creatorId: string,
  name: string,
  participantIds: string[],
  matchIds: string[],
  groupId?: string,
  participantEmails?: string[],
  picks?: PickPayload[],
  stakeCoins?: number,
  // When true, posts an open challenge (public board, no named opponent). specs/open-challenges.
  isOpen?: boolean
): {
  creatorId: string;
  name: string;
  participantIds: string[];
  participantEmails?: string[];
  matchIds: string[];
  picks?: PickPayload[];
  stakeCoins: number;
  exactScorePoints: number;
  correctOutcomePoints: number;
  groupId?: string;
  isOpen?: boolean;
} => {
  const cleanUserId =
    typeof creatorId === 'string' ? creatorId.trim() : creatorId;

  return {
    creatorId: cleanUserId,
    name,
    participantIds,
    participantEmails:
      participantEmails && participantEmails.length > 0
        ? participantEmails
        : undefined,
    matchIds,
    ...(picks && picks.length > 0 && { picks }),
    stakeCoins: stakeCoins ?? 0,
    exactScorePoints: 3,
    correctOutcomePoints: 1,
    ...(groupId && { groupId }),
    ...(isOpen && { isOpen: true }),
  };
};

/**
 * Extract match IDs from pick slips
 */
export const extractMatchIds = (
  pickSlips: Array<{ gameId: string }>
): string[] => {
  return pickSlips.map((bet) => bet.gameId);
};
